/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { LogoAzure, LogoBitbucket, LogoGitLab, LogoGitHub } from './icons';
import {
  AzureFields,
  FIELDS_THAT_REQUIRE_RESYNC as AzureFieldsThatRequireResync,
  FIELDS_VALIDATION as AzureFieldsValidation
} from './settings/fields/AzureFields';
import {
  BitbucketFields,
  FIELDS_THAT_REQUIRE_RESYNC as BitbucketFieldsThatRequireResync,
  FIELDS_VALIDATION as BitbucketFieldsValidation
} from './settings/fields/BitbucketFields';
import {
  GitHubFields,
  FIELDS_THAT_REQUIRE_RESYNC as GitHubFieldsThatRequireResync,
  FIELDS_VALIDATION as GitHubFieldsValidation
} from './settings/fields/GitHubFields';
import {
  GitLabFields,
  FIELDS_THAT_REQUIRE_RESYNC as GitLabFieldsThatRequireResync,
  FIELDS_VALIDATION as GitLabFieldsValidation
} from './settings/fields/GitLabFields';
import {
  FIELDS_THAT_REQUIRE_RESYNC as RepositoryFieldsThatRequireResync,
  FIELDS_VALIDATION as RepositoryFieldsValidation
} from './settings/fields/RepositoryDetailsFields';

export const SUPPORTED_GIT_PROVIDERS = {
  GITHUB: 'GITHUB',
  GITLAB: 'GITLAB',
  AZURE: 'AZURE',
  BITBUCKET: 'BITBUCKET'
};

export const DEFAULT_GIT_PROVIDER = SUPPORTED_GIT_PROVIDERS.GITHUB;

export const SupportedGitProviders = {
  [SUPPORTED_GIT_PROVIDERS.GITHUB]: {
    label: 'GitHub',
    icon: LogoGitHub,
    fields: GitHubFields,
    validationRules: { ...GitHubFieldsValidation, ...RepositoryFieldsValidation },
    fieldsThatRequireResync: [...GitHubFieldsThatRequireResync, ...RepositoryFieldsThatRequireResync],
    providerBaseUrl: 'https://github.com',
    branchPathPattern: ':repository/tree/:branch/:path',
    documentationUrl: 'https://docs.camunda.io/docs/next/components/modeler/web-modeler/git-sync/?platform=github'
  },
  [SUPPORTED_GIT_PROVIDERS.GITLAB]: {
    label: 'GitLab',
    icon: LogoGitLab,
    fields: GitLabFields,
    validationRules: { ...GitLabFieldsValidation, ...RepositoryFieldsValidation },
    fieldsThatRequireResync: [...GitLabFieldsThatRequireResync, ...RepositoryFieldsThatRequireResync],
    providerBaseUrl: 'https://gitlab.com',
    branchPathPattern: ':repository/-/tree/:branch/:path',
    documentationUrl: 'https://docs.camunda.io/docs/next/components/modeler/web-modeler/git-sync/?platform=gitlab'
  },
  [SUPPORTED_GIT_PROVIDERS.AZURE]: {
    label: 'Azure',
    icon: LogoAzure,
    fields: AzureFields,
    validationRules: { ...AzureFieldsValidation, ...RepositoryFieldsValidation },
    fieldsThatRequireResync: [...AzureFieldsThatRequireResync, ...RepositoryFieldsThatRequireResync],
    providerBaseUrl: 'https://dev.azure.com',
    branchPathPattern: ':repository?version=GB:branch&path=:path',
    documentationUrl: 'https://docs.camunda.io/docs/next/components/modeler/web-modeler/git-sync/?platform=azure'
  },
  [SUPPORTED_GIT_PROVIDERS.BITBUCKET]: {
    label: 'Bitbucket',
    icon: LogoBitbucket,
    fields: BitbucketFields,
    validationRules: { ...BitbucketFieldsValidation, ...RepositoryFieldsValidation },
    fieldsThatRequireResync: [...BitbucketFieldsThatRequireResync, ...RepositoryFieldsThatRequireResync],
    providerBaseUrl: 'https://bitbucket.org',
    branchPathPattern: ':repository/src/HEAD/:path?at=:branch',
    documentationUrl: 'https://docs.camunda.io/docs/next/components/modeler/web-modeler/git-sync/?platform=bitbucket'
  }
};
